import React, { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Link,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import FileIcon from "../../assets/image/file.svg";
import { formatTripDateTime } from "../../utils/formatDateTime";
import { useBackToList } from "../../hooks/useBackToList";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";
import TripMeterStatusBadge from "../../Components/TripMeterStatusBadge";
import { isTripMeterStopped } from "../../utils/meterSensorIssue";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

/** Accepts presigned S3 / CDN URLs; returns null if not usable in <img src>. */
const normalizeMediaUrl = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  return null;
};

/** Resolve single driver object from viewDriver response */
const pickDriverPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const data = payload.data;
  if (Array.isArray(data) && data[0] && typeof data[0] === "object") {
    return data[0];
  }
  if (data && typeof data === "object" && !Array.isArray(data)) {
    if (data.driver && typeof data.driver === "object") return data.driver;
    if (
      data.driver_data &&
      typeof data.driver_data === "object" &&
      !Array.isArray(data.driver_data)
    ) {
      return data.driver_data;
    }
    if (Array.isArray(data.driver_data) && data.driver_data[0]) {
      return data.driver_data[0];
    }
    return data;
  }
  if (payload.driver && typeof payload.driver === "object") return payload.driver;
  return null;
};

const displayOrDash = (v) => {
  if (v == null || v === "") return "—";
  return String(v);
};

const documentTypeLabel = (documentType) => {
  const k = String(documentType ?? "").toLowerCase();
  if (k === "driver_license") return "Driver license (front)";
  if (k === "driver_license_back") return "Driver license (back)";
  if (k.includes("adhar_card_front")) return "Aadhaar card (front)";
  if (k.includes("adhar_card_back")) return "Aadhaar card (back)";
  if (!k) return "Document";
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const pickTripHistoryRows = (payload) => {
  const data = payload?.data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.trip_history_data)) return data.trip_history_data;
  if (Array.isArray(data.trip_history)) return data.trip_history;
  if (Array.isArray(data.trips)) return data.trips;
  if (Array.isArray(data.trip_data)) return data.trip_data;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.list)) return data.list;
  return [];
};

/** Pick pagination meta from listOfTripHistory-style responses */
const pickTripHistoryMeta = (payload) => {
  const data = payload?.data;
  if (!data || typeof data !== "object") {
    return { total: 0, totalPages: 1 };
  }
  const total =
    typeof data.total === "number"
      ? data.total
      : Array.isArray(data.trip_history_data)
        ? data.trip_history_data.length
        : 0;
  const totalPages =
    typeof data.total_pages === "number" && data.total_pages >= 1
      ? data.total_pages
      : typeof data.totalPages === "number" && data.totalPages >= 1
        ? data.totalPages
        : Math.max(1, Math.ceil(total / (Number(data.per_page) || 10)));
  return { total, totalPages };
};

/** e.g. 2026-05-21T05:49:12.167Z → 21 May 2026 */
const formatDisplayDate = (v) => {
  if (v == null || v === "") return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return displayOrDash(v);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const mapTripRow = (trip, index) => {
  const company = trip.company_id;
  const companyLocation =
    company?.company_location_id?.name ??
    company?.company_location?.name ??
    trip.company_location_id?.name ??
    trip.company_location?.name;

  return {
    id: trip._id ?? trip.id ?? `trip-${index}`,
    tripId: displayOrDash(
      trip.trip_ref_id ?? trip.trip_id ?? trip.tripId ?? trip.ref_id
    ),
    company: displayOrDash(
      (typeof company === "object" && company?.name) ||
        trip.company_name ||
        trip.company
    ),
    location: displayOrDash(
      companyLocation ??
        trip.location_name ??
        trip.location ??
        trip.location_id?.name
    ),
    miplLocation: displayOrDash(
      trip.mipl_location_id?.name ??
        trip.mipl_location_name ??
        trip.mipl_location
    ),
    tripStartDateTime: formatTripDateTime(
      trip.trip_start_date_time ?? trip.start_date_time ?? trip.trip_start
    ),
    tripEndDateTime: formatTripDateTime(
      trip.trip_end_at ??
        trip.trip_end_date_time ??
        trip.end_date_time ??
        trip.trip_end
    ),
    vehicle: displayOrDash(
      trip.vehicle_id?.name ??
        trip.vehicle_number ??
        trip.vehicle_no ??
        trip.vehicle
    ),
    vehicleAvg: displayOrDash(
      trip.vehicle_average ??
        trip.vehicle_avg ??
        trip.average ??
        trip.avg
    ),
    meterStopped: isTripMeterStopped(trip),
  };
};

function DocumentThumb({ doc }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImgFailed, setViewerImgFailed] = useState(false);
  const showImg = Boolean(doc.url) && !imgFailed;
  const canView = Boolean(doc.url);

  useEffect(() => {
    setImgFailed(false);
  }, [doc.url]);

  useEffect(() => {
    if (viewerOpen) setViewerImgFailed(false);
  }, [viewerOpen, doc.url]);

  useEffect(() => {
    if (!viewerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setViewerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewerOpen]);

  useEffect(() => {
    if (!viewerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [viewerOpen]);

  const openViewer = () => {
    if (canView) setViewerOpen(true);
  };

  const viewer =
    viewerOpen && canView && typeof document !== "undefined"
      ? createPortal(
          <div
            className="fixed inset-0 z-[10050] flex items-center justify-center overflow-y-auto bg-black/75 p-4"
            role="dialog"
            aria-modal="true"
            aria-label={`View ${doc.label}`}
            onClick={() => setViewerOpen(false)}
          >
            <div
              className="relative max-h-[min(92vh,900px)] w-full max-w-4xl rounded-lg bg-white p-3 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2">
                <span className="text-sm font-UbuntuMedium capitalize text-gray-800">{doc.label}</span>
                <div className="flex items-center gap-2">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-UbuntuMedium text-[#000053] underline"
                  >
                    Open in new tab
                  </a>
                  <button
                    type="button"
                    className="rounded-md bg-gray-100 px-3 py-1.5 text-xs font-UbuntuMedium text-gray-700 hover:bg-gray-200"
                    onClick={() => setViewerOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="flex min-h-[min(36vh,320px)] max-h-[min(80vh,780px)] w-full items-center justify-center overflow-auto bg-gray-50 p-4">
                {!viewerImgFailed ? (
                  <img
                    key={doc.url}
                    src={doc.url}
                    alt=""
                    className="h-auto max-h-[min(76vh,720px)] w-auto max-w-full object-contain object-center"
                    referrerPolicy="no-referrer"
                    decoding="async"
                    onError={() => setViewerImgFailed(true)}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-3 py-12 text-gray-500">
                    <img src={FileIcon} className="w-12 opacity-50" alt="" />
                    <p className="text-sm">Preview unavailable. Use “Open in new tab”.</p>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div className="inline-flex w-48 flex-col overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
        <button
          type="button"
          className="group relative flex h-24 w-full shrink-0 items-center justify-center bg-white p-1 outline-none focus-visible:ring-2 focus-visible:ring-[#000053] focus-visible:ring-offset-2 disabled:cursor-not-allowed"
          onClick={openViewer}
          disabled={!canView}
          aria-label={canView ? `View ${doc.label}` : `${doc.label} — no file`}
        >
          {showImg ? (
            <img
              key={doc.url}
              src={doc.url}
              alt=""
              className="max-h-full max-w-full object-contain object-center"
              loading="lazy"
              referrerPolicy="no-referrer"
              decoding="async"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <img src={FileIcon} className="w-8 opacity-60" alt="" />
          )}
          {canView ? (
            <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-xs font-UbuntuMedium text-white opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
              Click to view
            </span>
          ) : null}
        </button>
        <span className="line-clamp-2 max-h-12 shrink-0 px-2 py-1.5 text-center text-xs capitalize text-gray-700">
          {doc.label}
        </span>
      </div>
      {viewer}
    </>
  );
}

const ViewDriverDetails = () => {
  const { setSubheading } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const driverId = searchParams.get("driver_id");
  const backToList = useBackToList("/driver-management");

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tripLoading, setTripLoading] = useState(false);
  const [tripRows, setTripRows] = useState([]);
  const [tripTotal, setTripTotal] = useState(0);
  const [tripTotalPages, setTripTotalPages] = useState(1);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setSubheading("View Driver Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!driverId?.trim()) {
        setDetail(null);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setDetail(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      const url = `${ACCOUNTANT_API}${endpoint.viewDriver}?driver_id=${encodeURIComponent(
        driverId.trim()
      )}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await parseBody(response);

        if (cancelled) return;

        if (!response.ok) {
          toast.error(getErrorMessage(payload, response));
          setDetail(null);
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          toast.error(getErrorMessage(payload, response));
          setDetail(null);
          return;
        }

        const d = pickDriverPayload(payload);
        setDetail(d);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load driver details."
          );
          setDetail(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [driverId]);

  const tripDriverIdRef = useRef(driverId);

  useEffect(() => {
    let cancelled = false;

    const loadTripHistory = async () => {
      if (!driverId?.trim()) {
        setTripRows([]);
        setTripTotal(0);
        setTripTotalPages(1);
        tripDriverIdRef.current = driverId;
        return;
      }

      const token = localStorage.getItem("sessionToken");
      if (!token) {
        setTripRows([]);
        setTripTotal(0);
        setTripTotalPages(1);
        tripDriverIdRef.current = driverId;
        return;
      }

      const driverSwitched = tripDriverIdRef.current !== driverId;
      if (driverSwitched) {
        tripDriverIdRef.current = driverId;
        if (currentPage !== 1) {
          setCurrentPage(1);
          return;
        }
      }

      setTripLoading(true);
      const pageToRequest = driverSwitched ? 1 : currentPage;
      const params = new URLSearchParams({
        driver_id: driverId.trim(),
        page: String(pageToRequest),
        limit: String(itemsPerPage),
      });
      const url = `${ACCOUNTANT_API}${endpoint.listOfTripHistory}?${params.toString()}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await parseBody(response);

        if (cancelled) return;

        if (!response.ok) {
          toast.error(getErrorMessage(payload, response));
          setTripRows([]);
          setTripTotal(0);
          setTripTotalPages(1);
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          toast.error(getErrorMessage(payload, response));
          setTripRows([]);
          setTripTotal(0);
          setTripTotalPages(1);
          return;
        }

        const list = pickTripHistoryRows(payload);
        const meta = pickTripHistoryMeta(payload);
        setTripRows(list.map(mapTripRow));
        setTripTotal(meta.total);
        setTripTotalPages(Math.max(1, meta.totalPages));
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load trip history."
          );
          setTripRows([]);
          setTripTotal(0);
          setTripTotalPages(1);
        }
      } finally {
        if (!cancelled) setTripLoading(false);
      }
    };

    loadTripHistory();
    return () => {
      cancelled = true;
    };
  }, [driverId, currentPage, itemsPerPage]);

  const driverDisplay = useMemo(() => {
    if (!detail || typeof detail !== "object") return null;
    const refId =
      detail.driver_ref_id ??
      detail.driverRefId ??
      detail.driver_ref ??
      null;
    const aadhaar =
      detail.adhar_card_number ??
      detail.aadhar_card_number ??
      detail.aadhaar_number ??
      detail.aadhaar ??
      null;
    const loc =
      detail.mipl_location_id?.name ??
      detail.mipl_location_name ??
      detail.location_name ??
      null;

    return {
      driverIdLabel:
        refId != null && refId !== "" ? `${refId}` : displayOrDash(detail._id),
      fullName: displayOrDash(detail.full_name ?? detail.name),
      aadhaar: displayOrDash(aadhaar),
      licence: displayOrDash(detail.license_number ?? detail.licence_number),
      licenseExpiryDate: formatDisplayDate(
        detail.license_expiry_date ??
          detail.licence_expiry_date ??
          detail.license_expiry ??
          detail.licence_expiry ??
          detail.expiry
      ),
      ntLicenseExpiryDate: formatDisplayDate(
        detail.nt_license_expiry_date ??
          detail.nt_licence_expiry_date ??
          detail.nt_license_expiry
      ),
      age: displayOrDash(detail.age),
      phone: [detail.country_code, detail.mobile_number ?? detail.phone]
        .filter(Boolean)
        .join(" ")
        || "—",
      location: displayOrDash(loc),
      accountantName: displayOrDash(
        detail.accountant_name ?? detail.accountant_id?.full_name
      ),
      joiningDate: formatDisplayDate(
        detail.join_date ??
          detail.joining_date ??
          detail.createdAt ??
          detail.created_at
      ),
      profileUrl: normalizeMediaUrl(
        detail.profile_image ??
          detail.profileImage ??
          detail.profile_photo ??
          detail.profile_url ??
          null
      ),
    };
  }, [detail]);

  const documentSlots = useMemo(() => {
    if (!detail || typeof detail !== "object") return [];
    let docs =
      detail.documents ??
      detail.document_list ??
      detail.id_proofs ??
      null;
    if (Array.isArray(detail.document_data)) {
      docs = detail.document_data;
    } else if (typeof detail.document_data === "string" && detail.document_data.trim()) {
      try {
        const parsed = JSON.parse(detail.document_data);
        if (Array.isArray(parsed)) {
          docs = parsed;
        }
      } catch {
        /* ignore */
      }
    }
    if (!Array.isArray(docs)) return [];
    return docs.map((item, i) => {
      const rawUrl =
        item.document_image ??
        item.Document_Image ??
        item.documentImage ??
        item.document_image_url ??
        item.url ??
        item.file_url ??
        item.image ??
        item.path ??
        null;
      return {
        key: item._id ?? `${item.document_type ?? item.type ?? "doc"}-${i}`,
        label:
          documentTypeLabel(item.document_type) ||
          item.type ||
          item.name ||
          `Document ${i + 1}`,
        url: normalizeMediaUrl(rawUrl),
      };
    });
  }, [detail]);

  const goBack = () => {
    navigate(-1);
  };

  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const currentTrips = tripRows;
  const totalPages = tripTotalPages;

  return (
    <>
        <div className='col-md-4'>
      <Link to={backToList}  className="btn btn-primary shrink-0 mt-4">Back</Link>
    </div>
      {!driverId?.trim() && (
        <div className="text-center mt-7 text-gray-600 px-4">
          Missing driver. Open this page from the driver list (eye icon).
          <div className="mt-3">
            <Link className="text-primary underline" to={backToList}>
              Back to Driver Management
            </Link>
          </div>
        </div>
      )}

      {driverId?.trim() && loading && (
        <div className="text-center mt-7 text-gray-500">Loading driver details…</div>
      )}

      {driverId?.trim() && !loading && !detail && (
        <div className="text-center mt-7 text-gray-600">
          Could not load driver details.
          <div className="mt-3">
            <button type="button" className="text-primary underline" onClick={goBack}>
              Go back
            </button>
          </div>
        </div>
      )}

      {driverId?.trim() && !loading && driverDisplay && (
        <>
          <div className="text-center mt-7">
            <div className="inline-flex h-40 w-40 items-center justify-center overflow-hidden rounded-full bg-gray-200 mx-auto">
              {driverDisplay.profileUrl ? (
                <img
                  src={driverDisplay.profileUrl}
                  alt=""
                  className="max-h-full max-w-full object-contain object-center"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  decoding="async"
                />
              ) : null}
            </div>
            <p className="mt-3 mb-0 text-lg font-UbuntuMedium text-gray-900">
              {driverDisplay.fullName}
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <h3 className="text-xl font-UbuntuMedium !mb-2">Personal Details</h3>
            <div className="max-w-screen-lg rounded-lg px-4 py-2 bg-white mb-4 shadow-sm">
              <ul className="m-0 grid list-none grid-cols-2 p-0 font-UbuntuMedium [&_li]:flex [&_li]:items-start [&_li]:justify-between [&_li]:gap-4 [&_li]:py-1.5 [&_li:nth-child(odd)]:border-r [&_li:nth-child(odd)]:border-gray-200 [&_li:nth-child(odd)]:pr-12 [&_li:nth-child(even)]:pl-12 [&_.label]:shrink-0 [&_.label]:text-gray-400 [&_.value]:min-w-0 [&_.value]:text-right [&_.value]:break-words">
                <li>
                  <div className="label">Driver ID</div>
                  <div className="value font-UbuntuMedium">{driverDisplay.driverIdLabel}</div>
                </li>
                <li>
                  <div className="label">Joining Date</div>
                  <div className="value font-UbuntuMedium">{driverDisplay.joiningDate}</div>
                </li>
                <li>
                  <div className="label">Licence Number</div>
                  <div className="value font-UbuntuMedium">{driverDisplay.licence}</div>
                </li>
                <li>
                  <div className="label">Accountant Name</div>
                  <div className="value font-UbuntuMedium">{driverDisplay.accountantName}</div>
                </li>
                <li>
                  <div className="label">NT License Expiry Date</div>
                  <div className="value font-UbuntuMedium">{driverDisplay.ntLicenseExpiryDate}</div>
                </li>
                <li>
                  <div className="label">Phone Number</div>
                  <div className="value font-UbuntuMedium">{driverDisplay.phone}</div>
                </li>
                <li>
                  <div className="label">TR License Expiry Date</div>
                  <div className="value font-UbuntuMedium">{driverDisplay.licenseExpiryDate}</div>
                </li>
                <li>
                  <div className="label">Aadhar Card number</div>
                  <div className="value font-UbuntuMedium">{driverDisplay.aadhaar}</div>
                </li>
                <li>
                  <div className="label">MIPL Location</div>
                  <div className="value font-UbuntuMedium">{driverDisplay.location}</div>
                </li>
                <li>
                  <div className="label">Age</div>
                  <div className="value font-UbuntuMedium">{driverDisplay.age}</div>
                </li>
              </ul>
            </div>
            <h3 className="text-xl font-UbuntuMedium !mb-2">Id Proof Documents</h3>
            <div className="flex gap-x-4 flex-wrap">
              {documentSlots.length === 0 ? (
                <p className="text-gray-500 text-sm">No documents returned by API.</p>
              ) : (
                documentSlots.map((doc) => (
                  <DocumentThumb key={doc.key} doc={doc} />
                ))
              )}
            </div>
          </div>
        </>
      )}

      <h3 className="text-xl font-UbuntuMedium !mb-2 mt-8 max-w-5xl mx-auto px-4">
        Complete Trip History
      </h3>
      <div className="common-table-wrapper m-0 shadow-sm max-w-5xl mx-auto">
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Trip ID</th>
                <th>Company</th>
                <th>Location</th>
                <th>MIPL Location</th>
                <th>Trip Start Date & Time</th>
                <th>Trip End Date & Time</th>
                <th>Vehicle</th>
                <th>Vehicle Avg</th>
                <th>Meter</th>
              </tr>
            </thead>

            <tbody>
              {tripLoading ? (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-gray-500">
                    Loading trip history…
                  </td>
                </tr>
              ) : currentTrips.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-gray-500">
                    No trip history available.
                  </td>
                </tr>
              ) : (
                currentTrips.map((driver, index) => (
                  <tr key={driver.id}>
                    <td>{indexOfFirst + index + 1}</td>
                    <td>{driver.tripId}</td>
                    <td>{driver.company}</td>
                    <td>{driver.location}</td>
                    <td>{driver.miplLocation}</td>
                    <td>{driver.tripStartDateTime}</td>
                    <td>{driver.tripEndDateTime}</td>
                    <td>{driver.vehicle}</td>
                    <td>{driver.vehicleAvg}</td>
                    <td className="whitespace-nowrap">
                      <TripMeterStatusBadge stopped={driver.meterStopped} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex gap-x-4 items-center mt-4 max-w-5xl mx-auto px-4 pb-8">
        <select
          className="p-2 shadow-sm outline-none"
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
        </select>
        <div className="text-sm text-gray-500 ml-auto">{tripTotal} records found</div>
        <div className="flex items-center gap-2 pr-8">
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1 || tripTotal === 0}
            className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
              currentPage === 1 ? "" : "cursor-pointer"
            }`}
          >
            <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
          </button>
          <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
          <span className="text-sm text-gray-600">of {totalPages}</span>
          <button
            type="button"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages || tripTotal === 0}
            className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
              currentPage === totalPages ? "" : "cursor-pointer"
            }`}
          >
            <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
          </button>
        </div>
      </div>

      <Toastify />
    </>
  );
};

export default ViewDriverDetails;
